import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  TbArrowLeft,
  TbUser,
  TbMail,
  TbPhone,
  TbBuilding,
  TbWorld,
  TbMapPin,
  TbMessage,
  TbCalendar,
} from "react-icons/tb";

import { AxiosInstance } from "../../services/Api";
import styles from "./RequestDemoView.module.css";

const InfoField = ({ icon: Icon, label, value }) => (
  <div className={styles.field}>
    <div className={styles.fieldHeader}>
      {Icon && (
        <span className={styles.fieldIcon}>
          <Icon size={16} />
        </span>
      )}
      <span className={styles.fieldLabel}>{label}</span>
    </div>
    <div className={styles.fieldValue}>{value || "—"}</div>
  </div>
);

const RequestDemoView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Guard so we only mark as read once per mount ───
  const markedAsReadRef = useRef(false);

  // ─── Mark as read (silent background call) ───
  const markAsRead = useCallback(async () => {
    if (!id || markedAsReadRef.current) return;
    markedAsReadRef.current = true;

    try {
      const response = await AxiosInstance.post("/roll/demo-update", {
        id: Number(id),
        is_active: 1,
        updated_by: "1",
      });
      console.log("✅ Marked as read:", response.data);

      // Reflect new status locally so the UI shows "Read"
      setDemo((prev) => (prev ? { ...prev, isActive: 1 } : prev));
    } catch (err) {
      // Don't disrupt the user — just log it
      console.error("⚠️ Failed to mark as read:", err);
      markedAsReadRef.current = false; // allow retry if it failed
    }
  }, [id]);

  // ─── Fetch view details ───
  const fetchDemoDetails = useCallback(async () => {
    console.log("🟡 fetchDemoDetails id:", id);

    if (!id) {
      setError("No demo request ID provided.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await AxiosInstance.post("/roll/demo-view", {
        id: Number(id),
      });
      console.log("🟢 View API response:", response.data);

      if (response.data?.success && response.data.data) {
        const detail =
          response.data.data.demo ||
          response.data.data.detail ||
          response.data.data;
        setDemo(detail);

        // ✅ After successful view → call update API to mark as read
        // Only call if currently unread (isActive === 0) to avoid redundant updates
        if (Number(detail?.isActive) === 0) {
          markAsRead();
        }
      } else {
        throw new Error(
          response.data?.message || "Failed to fetch demo details",
        );
      }
    } catch (err) {
      console.error("🔴 Error fetching demo details:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load demo details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [id, markAsRead]);

  useEffect(() => {
    fetchDemoDetails();
  }, [fetchDemoDetails]);

  // 🔧 Fixed: back should go to the demo list, not contact list
  const handleBack = () => navigate("/dashboard/masters/request-demo");

  const fullName = demo
    ? [demo.first_name, demo.middle_name, demo.last_name]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <div className={styles.container}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={handleBack}
        >
          <TbArrowLeft size={18} />
          <span>Back to List</span>
        </button>
        <h1 className={styles.title}>DEMO REQUEST DETAILS</h1>
      </div>

      {/* ── Body ── */}
      {loading ? (
        <div className={styles.stateBox}>
          <div className={styles.spinner} />
          <p>Loading demo details...</p>
        </div>
      ) : error ? (
        <div className={styles.stateBox}>
          <p className={styles.errorMessage}>{error}</p>
          <button
            type="button"
            onClick={fetchDemoDetails}
            className={styles.retryButton}
          >
            Retry
          </button>
        </div>
      ) : demo ? (
        <>
          {/* Profile strip */}
          <div className={styles.profileCard}>
            <div className={styles.avatar}>
              {fullName
                ? fullName
                    .split(" ")
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()
                : "?"}
            </div>
            <div className={styles.profileInfo}>
              <h2 className={styles.profileName}>{fullName || "Unnamed"}</h2>
              <div className={styles.profileMeta}>
                {demo.institution_name && (
                  <span>
                    <TbBuilding size={14} /> {demo.institution_name}
                  </span>
                )}
                {demo.email && (
                  <span>
                    <TbMail size={14} /> {demo.email}
                  </span>
                )}
              </div>
            </div>
            {demo.id != null && (
              <div className={styles.idBadge}>#{demo.id}</div>
            )}
          </div>

          {/* Detail grid */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Personal Information</h3>
            <div className="row g-3">
              <div className="col-12 col-md-6 col-lg-4">
                <InfoField
                  icon={TbUser}
                  label="First Name"
                  value={demo.first_name}
                />
              </div>
              {demo.middle_name && (
                <div className="col-12 col-md-6 col-lg-4">
                  <InfoField
                    icon={TbUser}
                    label="Middle Name"
                    value={demo.middle_name}
                  />
                </div>
              )}
              <div className="col-12 col-md-6 col-lg-4">
                <InfoField
                  icon={TbUser}
                  label="Last Name"
                  value={demo.last_name}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <InfoField icon={TbMail} label="Email" value={demo.email} />
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <InfoField
                  icon={TbPhone}
                  label="Phone"
                  value={demo.phone || demo.phone_number}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <InfoField
                  icon={TbBuilding}
                  label="Institution"
                  value={demo.institution_name}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <InfoField
                  icon={TbMapPin}
                  label="Address"
                  value={demo.address}
                />
              </div>

              {demo.created_at && (
                <div className="col-12 col-md-6 col-lg-4">
                  <InfoField
                    icon={TbCalendar}
                    label="Requested On"
                    value={new Date(demo.created_at).toLocaleDateString(
                      "en-GB",
                    )}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Message — full width */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Message</h3>
            <div className="row g-3">
              <div className="col-12">
                <div className={styles.messageBox}>
                  <span className={styles.fieldIcon}>
                    <TbMessage size={16} />
                  </span>
                  <p className={styles.messageText}>
                    {demo.message || "No message provided."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.stateBox}>
          <p>No demo request data found.</p>
        </div>
      )}
    </div>
  );
};

export default RequestDemoView;
